import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment as env } from "../../../../../environments/environment.prod";
import { AbstractService } from "../../../../services/abstract.service";
import {
    andWhere,
    buildQuery,
    CombinedFilter,
    FilterFn,
    noop,
    Ops,
    orderBy,
    OrderVariant,
    orWhere,
    page,
    select,
    where,

  } from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
//   import { CombinedFilter } from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import BusinessPartnerAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner/business-partner.entity";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { IPagination, IPaginationResult } from "app/common/interfaces";
import { Observable } from "rxjs";

// const apiUrl = env.API_NEW_BUSINESS_PARTNER_URL;
const newApiUrl = env.API_BUSINESS_PARTNER;
const moduleName = "business-partner";

@Injectable({
  providedIn: "root",
})
export class BusinessPartnerNewsService extends AbstractService<
    BusinessPartnerAttributes,
    any,
    any
    > {

    /** The last filter used to load the list */
    currentFilter: CombinedFilter<BusinessPartnerAttributes>;
    
    constructor(
        http: HttpClient,
    ) {
        super(newApiUrl, moduleName, http);
    }

    findByFilter(filter: CombinedFilter<BusinessPartnerAttributes>, options?: RequestOptions) {
          const API_URL: string =
            newApiUrl +
            `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
            const headers: Record<string, any> = {};
            // see order-customer-export for more info about this header
            // ctx: stands for context
            if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
            return this.http.get<IPaginationResult<BusinessPartnerAttributes>>(API_URL, { headers });
        }
    
      /**
       * Obtiene nuevos prospectos de socios de negocio con paginación y filtros.
       *
       * Estrategia de búsqueda:
       * - Sin búsqueda: Un solo objeto con filtros base
       * - Con búsqueda: Tres objetos (OR lógico) - busca en 'name', 'externalKeys.code', 'types.name'
       *
       * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
       *
       * @param options Pagination options
       * @param companyIds Company filter
       * @param typesCodes Types codes filter
       * @param flagBusinessPartner Flag for business partner type filtering
       * @param salesOfficeId Sales office filter
       * @returns Paginated data
       *
       * Search fields: name, externalKeys.code, types.name
       * Array filters: companyIds, typesCodes
       * Single filters: flagBusinessPartner, salesOfficeId
       */
      findAllWithCount(
        options: IPagination<BusinessPartnerAttributes>,
        companyIds: number[],
        typesCodes: string[],
        flagBusinessPartner: boolean,
        salesOfficeId?
      ): Observable<IPaginationResult<BusinessPartnerAttributes>> {

        const hasSearch = !!options.search;

        /**
         * Construye las condiciones base que SIEMPRE deben aplicarse.
         * Incluye: _deleted=false, y todos los filtros condicionales.
         *
         * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
         */
        const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<BusinessPartnerAttributes>[] => {
          const deletedFilter: FilterFn<BusinessPartnerAttributes> = params.useOrWhere
            ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
            : where('_deleted', Ops.eq(false));   // Primer objeto del array

          const filters: FilterFn<BusinessPartnerAttributes>[] = [deletedFilter];

          // Filtro de códigos de tipos
          if (typesCodes && typesCodes.length > 0) {
            filters.push(andWhere("types.code" as keyof BusinessPartnerAttributes, Ops.in(...typesCodes)));
          }

          // Filtro de flag business partner
          if (flagBusinessPartner) {
            filters.push(andWhere("types.code" as keyof BusinessPartnerAttributes, Ops.in(...['BPT0004'])));
          } else {
            filters.push(andWhere("types.code" as keyof BusinessPartnerAttributes, Ops.notIn(...['BPT0004'])));
          }

          // Filtro de compañías
          if (companyIds && companyIds.length > 0) {
            filters.push(andWhere('companies.id' as keyof BusinessPartnerAttributes, Ops.in(...companyIds)));
          }

          // Filtro de oficina de ventas
          if (salesOfficeId) {
            filters.push(andWhere('routingSalesOffice.id' as keyof BusinessPartnerAttributes, Ops.eq(salesOfficeId)));
          }

          return filters;
        };

        // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

        // Objeto 1: Condiciones base + búsqueda por NAME (o solo condiciones si no hay búsqueda)
        const searchByNameQueryObject: FilterFn<BusinessPartnerAttributes>[] = [
          ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
          hasSearch ? andWhere("name", Ops.like(options.search!, "i")) : noop()
        ];

        // Objeto 2: Condiciones base + búsqueda por EXTERNAL KEYS CODE (solo si hay búsqueda)
        const searchByExternalCodeQueryObject: FilterFn<BusinessPartnerAttributes>[] = hasSearch ? [
          ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
          andWhere("externalKeys.code" as keyof BusinessPartnerAttributes, Ops.like(options.search!, "i"))
        ] : [];

        // Objeto 3: Condiciones base + búsqueda por TYPES NAME (solo si hay búsqueda)
        const searchByTypesNameQueryObject: FilterFn<BusinessPartnerAttributes>[] = hasSearch ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere("types.name" as keyof BusinessPartnerAttributes, Ops.like(options.search!, "i"))
        ] : [];

        // ========== ENSAMBLAJE FINAL ==========

        const query = buildQuery<BusinessPartnerAttributes>(
          // Expandimos los tres objetos en el buildQuery
          ...searchByNameQueryObject,
          ...searchByExternalCodeQueryObject,
          ...searchByTypesNameQueryObject,

          // Ordenamiento y paginación
          orderBy(
            options.sort ?? "name",
            options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
          ),
          page(options.currentPage ?? 1, options.limitPerPage ?? 100)
        );

        this.currentFilter = query;

        const API_URL: string =
          newApiUrl +
          `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
        return this.http.get<IPaginationResult<BusinessPartnerAttributes>>(API_URL);
      }

  // Para crear un nuevo socio
  createnewBP(dataToSend) {
    const API_URL: string = newApiUrl + `api/${moduleName}/new_bp`
    return this.http.post(API_URL, dataToSend);
  }

  updatenewBP(id:string, dataToUpdate) {
    const API_URL: string = newApiUrl + `api/${moduleName}/new_bp/${id}`
    return this.http.patch(API_URL, dataToUpdate);
  }
}
